import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import type { User, Account } from "./types";
import { authMe, listAccounts, listPendingInvitations } from "./api";
import { ApiError } from "./errors";
import { firebaseSignOut } from "./firebase";

const ACTIVE_ACCOUNT_KEY = "ocm_active_account_id";

interface AuthState {
  user: User | null;
  account: Account | null;
  accounts: Account[];
  loading: boolean;
  accountError: boolean;
  isAdmin: boolean;
  pendingInvitationCount: number;
  setAccount: (account: Account) => void;
  refreshAccounts: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthState>({
  user: null,
  account: null,
  accounts: [],
  loading: true,
  accountError: false,
  isAdmin: false,
  pendingInvitationCount: 0,
  setAccount: () => {},
  refreshAccounts: async () => {},
  logout: () => {},
});

function redirectToApprovalLogin(error: ApiError) {
  const currentPath = `${window.location.pathname}${window.location.search}`;
  const currentParams = new URLSearchParams(window.location.search);
  const loginParams = new URLSearchParams();
  loginParams.set("error", error.message);

  const existingReturn = currentParams.get("return");
  if (existingReturn) {
    loginParams.set("return", existingReturn);
  } else if (window.location.pathname !== "/login" && currentPath.startsWith("/") && !currentPath.startsWith("//")) {
    loginParams.set("return", currentPath);
  }

  window.location.href = `/login?${loginParams.toString()}`;
}

export function AuthProvider({ children, enabled = true }: { children: ReactNode; enabled?: boolean }) {
  const [user, setUser] = useState<User | null>(null);
  const [account, setAccount] = useState<Account | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [accountError, setAccountError] = useState(false);
  const [pendingInvitationCount, setPendingInvitationCount] = useState(0);

  const selectAccount = useCallback((acct: Account) => {
    setAccount(acct);
    localStorage.setItem(ACTIVE_ACCOUNT_KEY, String(acct.id));
  }, []);

  const fetchAccounts = useCallback(async () => {
    try {
      const accts = await listAccounts() ?? [];
      setAccounts(accts);
      if (accts.length > 0) {
        const savedId = localStorage.getItem(ACTIVE_ACCOUNT_KEY);
        const restored = savedId ? accts.find(a => a.id === Number(savedId)) : null;
        setAccount(restored ?? accts[0]);
      }
      setAccountError(false);
    } catch {
      setAccountError(true);
    }
  }, []);

  const fetchPendingInvitations = useCallback(async () => {
    try {
      const invs = await listPendingInvitations();
      setPendingInvitationCount(invs?.length ?? 0);
    } catch {
      // Non-critical
    }
  }, []);

  const refreshAccounts = useCallback(async () => {
    await Promise.all([fetchAccounts(), fetchPendingInvitations()]);
  }, [fetchAccounts, fetchPendingInvitations]);

  useEffect(() => {
    if (!enabled) return;

    authMe()
      .then(async (res) => {
        setUser(res.user);
        await refreshAccounts();
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.code === "approval_required") {
          const alreadyShowingApproval =
            window.location.pathname === "/login" &&
            new URLSearchParams(window.location.search).has("error");
          if (!alreadyShowingApproval) {
            redirectToApprovalLogin(err);
          }
        }
        // No valid session — user needs to login
      })
      .finally(() => setSessionChecked(true));
  }, [enabled, refreshAccounts]);

  // Derived, not stored: when a route flips auth from disabled to enabled
  // (public page → protected page, client-side), consumers must see
  // loading=true on that same render — an effect-driven setLoading would
  // leave a one-render window where ProtectedRoute redirects to /login
  // despite a valid session cookie.
  const loading = enabled && !sessionChecked;

  const logout = async () => {
    setUser(null);
    setAccount(null);
    setAccounts([]);
    localStorage.removeItem(ACTIVE_ACCOUNT_KEY);

    // Tell backend to clear ocm_token cookie
    const apiBase = import.meta.env.VITE_API_URL || "/api";
    try {
      await fetch(`${apiBase}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // Best-effort
    }

    // Clear ocm_token cookie client-side as fallback
    const domains = ["", ".openclawmachines.com"];
    for (const domain of domains) {
      const domainPart = domain ? `; domain=${domain}` : "";
      document.cookie = `ocm_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/${domainPart}`;
    }

    // Sign out of Firebase
    try {
      await firebaseSignOut();
    } catch {
      // Best-effort
    }

    window.location.href = "/signed-out";
  };

  const isAdmin = user?.email === "mathewma@gmail.com";

  return (
    <AuthContext.Provider value={{
      user, account, accounts, loading, accountError, isAdmin,
      pendingInvitationCount, setAccount: selectAccount, refreshAccounts, logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
